import { ref } from 'vue';
import { useLocale } from './useLocale';

import { get } from '@/api/httpClient';
import { ListingTypeEnum } from '@/enums/ListingType';
import type { BuildingRenderLayer } from '@/typings/drawing/BuildingRenderLayer';
import type { Project } from '@/typings/projects/Project';
import type { GetProjectsRequest } from '@/typings/requests/GetProjectsRequest';
import type { GetProjectRenderCanvasRequest } from '../typings/requests/GetProjectRenderCanvasRequest';

export function useProjectsApi() {
  // GET refs
  const getAllProjectsError = ref<string | undefined>();
  const getAllProjectsLoading = ref(false);
  const getProjectsByRegionIdError = ref<string | undefined>();
  const getProjectsByRegionIdLoading = ref(false);
  const getProjectBuildingCanvasError = ref<string | undefined>();
  const getProjectBuildingCanvasLoading = ref(false);
  const getProjectByIdError = ref<string | undefined>();
  const getProjectByIdLoading = ref(false);
  const getProjectRenderCanvasError = ref<string | undefined>();
  const getProjectRenderCanvasLoading = ref(false);
  const { getLocale } = useLocale();
  

  // GET methods
  async function getAllProjects(hasPromotion?: boolean, listingType?: ListingTypeEnum, limit?: number): Promise<Project[]> {
    getAllProjectsLoading.value = true;
    try {
      const params: { hasPromotion?: boolean; listingType?: ListingTypeEnum; limit?: number } = {};
      if (hasPromotion !== undefined) {
        params.hasPromotion = hasPromotion;
      }
      if (listingType !== undefined) {
        params.listingType = listingType;
      }
      if (limit !== undefined) {
        params.limit = limit;
      }
      const lang = getLocale();

      const query = { ...(params as Record<string, any>), locale: lang };
      return await get<Record<string, any>, Project[]>('/projects', query);
    } catch (err) {
      getAllProjectsError.value = err instanceof Error ? err.message : 'Unknown error';
      return [];
    } finally {
      getAllProjectsLoading.value = false;
    }
  }

  async function getProjectsByRegion(regionIds: number[], request?: GetProjectsRequest): Promise<Project[]> {
    getProjectsByRegionIdLoading.value = true;
    try {
      let projects;
      const lang = getLocale();
      const reqWithLocale = { ...(request as any), locale: lang };
      if (regionIds.length === 1) {
        projects = await get<any, Project[]>(`/projects/region/${regionIds[0]}`, reqWithLocale);
      } else {
        projects = await get<any, Project[]>('/projects/region', reqWithLocale);
      }
      return projects;
    } catch (err) {
      getProjectsByRegionIdError.value = err instanceof Error ? err.message : 'Unknown error';
      return [];
    } finally {
      getProjectsByRegionIdLoading.value = false;
    }
  }

  async function getProjectById(id: number): Promise<Project> {
    getProjectByIdLoading.value = true;
    try {
      const lang = getLocale();
      return await get<undefined, Project>(`/projects/${id}?locale=${lang}`);
    } catch (err) {
      getProjectByIdError.value = err instanceof Error ? err.message : 'Unknown error';
      throw err;
    } finally {
      getProjectByIdLoading.value = false;
    }
  }

  async function getProjectRenderCanvas(projectId: number, request?: GetProjectRenderCanvasRequest): Promise<BuildingRenderLayer[]> {
    getProjectRenderCanvasError.value = undefined;
    getProjectRenderCanvasLoading.value = true;
    try {
      const lang = getLocale();
      const reqWithLocale = { ...(request as any), locale: lang };
      const response = await get<any, BuildingRenderLayer[]>(`/projects/canvas/${projectId}`, reqWithLocale);
      return response;
    }
    catch (err) {
      getProjectRenderCanvasError.value = err instanceof Error ? err.message : 'Unknown error';
      return [];
    }
    finally {
      getProjectRenderCanvasLoading.value = false;
    }
  }
  
  return {
    // GET returns
    getAllProjects,
    getAllProjectsError,
    getAllProjectsLoading,
    getProjectsByRegion,
    getProjectsByRegionIdError,
    getProjectsByRegionIdLoading,
    getProjectBuildingCanvasError,
    getProjectBuildingCanvasLoading,
    getProjectById,
    getProjectByIdError,
    getProjectByIdLoading,
    getProjectRenderCanvas,
    getProjectRenderCanvasError,
    getProjectRenderCanvasLoading,
  };
}
